import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AiAssistantFormGroup} from "../create-ai-assistant-form-data";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {
  AiTemplateInputParams
} from "../../../../../../ai-assistant/components/ai-assistant-templates/models/ai-template-params.model";
import {filter, first, takeUntil} from "rxjs/operators";
import {ActivityAction, ActivityStatus} from "../../../../../../activities/activity";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../../store';
import {Subject} from "rxjs";
import {ProductService} from "../../../../../../websites/website/product.service";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import {TypeUtils} from "../../../../../../shared/utils/type-utils";
import {NotifierComponent} from "../../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../../shared/components/notifier/notification-type";
import {User} from "../../../../../../user/user";
import {CookieService} from "ngx-cookie";
import {DbResponse} from "../../../../../../shared/interceptors/db-response";
import {AiBuilderService} from "../../../../../../websites/website/ai-builder/service/ai-builder.service";
import {Config} from "../../../../../../config/config";
import {ReCaptchaV3Service} from "ngx-captcha";
import {DbValidators} from "../../../../../../shared/validators/DbValidators";
import {ActivatedRoute} from "@angular/router";
import {CreateAiAssistantService} from "../create-ai-assistant.service";

@Component({
    selector: 'db-create-ai-assistant-form-view',
    templateUrl: './create-ai-assistant-form-view.component.html',
    styleUrls: ['./create-ai-assistant-form-view.component.scss'],
    standalone: false
})
export class CreateAiAssistantFormViewComponent implements OnChanges, OnInit, OnDestroy {

  @Input() currentStep = 0;
  @Input() previousStep = 0;
  @Input() stepsLength = 0;
  @Input() formGroupData: AiAssistantFormGroup;
  @Input() recreationProcess = false;
  @Input() showHigDemandNotice = false;
  @Input() recreationFinished = false;
  @Input() isOnboarding = false;
  @Input() isFromGenerate = false;
  @Input() forRecreateFirstTime = false;
  @Input() oldFormData: Record<string, string | number> = {};
  @Input() frontUrl: string;
  @Input() websiteId: number;
  @Input() user: User;
  @Input() isEcommerce: boolean;
  @Input() hasTemplatesPage: boolean;
  @Input() signUpWithHubspot?: boolean = false;
  @Input() fromLanding?: boolean = false;

  @Output() next = new EventEmitter<Record<string, string>>();
  @Output() back = new EventEmitter<Record<string, string>>();
  @Output() redirect =  new EventEmitter<void>();
  @Output() blogType ? =  new EventEmitter<boolean>();
  public formGroup: FormGroup;
  public formSubmitted = false;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public isMobile: boolean;
  public loading: boolean;
  public generatedServices: any = null;
  public generateDescriptionLoading: boolean;
  public serverErrors: any;
  public showMetaTitle: boolean = false;
  public showLoading: boolean = false;
  public showDomainInput: string = '';
  public domainInfo: string = '';
  public domainNameKey: string = 'domain_name';
  public addStepsCount = 0;
  public hasFilledDomainName: boolean;
  public  TypeUtils = TypeUtils;

  constructor(
    public createAiAssistantService: CreateAiAssistantService,
    private _store: Store<fromStore.DbStoreState>,
    private _productService: ProductService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private _builderService: AiBuilderService,
    private _cookieService: CookieService,
    private _route: ActivatedRoute,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formGroupData'] && changes['formGroupData'].currentValue && changes['formGroupData'].currentValue.inputs) {
      this.formGroupData = changes['formGroupData'].currentValue;
      const queryParams: any = (<any>this._route.queryParams).value;
      const fromDomains = queryParams.fromDomains;
      if (localStorage.getItem('dbOnboardingDomainName') && localStorage.getItem('dbFromDomains')) {
        this.hasFilledDomainName = true;
      }
      this.createInputs(changes['formGroupData'].currentValue.inputs);
    }
  }
  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;
    this._getRecreateOngoingActivity();
    this._formGroupValueChanges();
    this.addStepsCount = this.hasTemplatesPage ? 2 : 1;
  }

  private _formGroupValueChanges(): void {
    this.formGroup && this.formGroup.valueChanges.subscribe((changedValue: any) => {
      this.serverErrors = null;
    });
  }

  private createInputs(inputs: AiTemplateInputParams[]): void {
    // Check if data is still loading, and if so, delay form creation
    if (this.createAiAssistantService.isDataLoading.getValue()) {
      setTimeout(() => {
        this.createInputs(inputs);
      }, 100);
      return;
    }

    // add counter for required content
    this.formSubmitted = false;
    const controls = {};
    inputs.forEach(input => controls[input.name] = this.createFormControl(input));
    this.formGroup = new FormGroup(controls);
    if (this.hasFilledDomainName && this.formGroup.get('domain_name')) {
      this.showDomainInput = 'no';
      const domainNameFromLocalStorage = localStorage.getItem('dbOnboardingDomainName');
      if (domainNameFromLocalStorage !== null && domainNameFromLocalStorage !== undefined) {
        this.formGroup.get('domain_name').setValue(domainNameFromLocalStorage);
      }
    }
    if (this.generatedServices) {
      this._fillInputValue(this.generatedServices);
    }
  }

  private createFormControl(input: AiTemplateInputParams): FormControl {
    const validators = [];
    validators.push(DbValidators.trimStart);
    input.required && validators.push(Validators.required);
    input.min_character_limit && validators.push(Validators.minLength(input.min_character_limit));
    input.character_limit && validators.push(Validators.maxLength(input.character_limit));

    let value;

    // Get prefilled data from service to ensure we have the latest
    const prefilledData = this.createAiAssistantService.prefilledAiData.getValue();

    if (prefilledData) {
      // Logic for prefilledAiData flow - prioritize service data
      if (input.field_type === 'text' && input.name !== this.domainNameKey) {
        value = prefilledData['business_name'] ? String(prefilledData['business_name']) : '';
      } else if (input.field_type === 'textarea') {
        value = prefilledData['site_description'] ? String(prefilledData['site_description']) : '';
      } else if (input.name in prefilledData) {
        value = prefilledData[input.name] ? String(prefilledData[input.name]) : '';
      }
    }

    // If not in prefilledAiData flow or value wasn't set, use original logic
    if (value === undefined) {
      // Original logic for other flows
      value = this.oldFormData[input.name] || input.default || '';

      // If in the landing flow, override value based on field type
      if (this.fromLanding) {
        if (input.field_type === 'text' && input.name !== this.domainNameKey) {
          value = this.oldFormData['business_name'] || input.default || '';
        } else if (input.field_type === 'textarea') {
          value = this.oldFormData['site_description'] || input.default || '';
        }
      }
    }

    return new FormControl(value, validators);
  }


  public proceedToNextQuestion(finalize = false): void {
    if (this.showDomainInput === '' && this.formGroup.get('domain_name')) {
      return;
    }
    if (this.signUpWithHubspot && this.currentStep + 1 === this.stepsLength) {
      this.showLoading = true;
    }
    this.formSubmitted = true;
    if (this.formGroup.invalid) return;
    if (TypeUtils.isDefined(this.formGroup) && TypeUtils.isDefined(this.formGroup.value) && !TypeUtils.isEmpty(this.formGroup.value)) {
      const label = JSON.stringify(Object.keys(this.formGroup.value));
      this._sendEvent(this.isOnboarding ? 'Onboarding' : 'Dashboard Action', label);
    }
    finalize && (this.loading = true);
    const businessType = (this.formGroup.get('business_type') && this.formGroup.get('business_type').value) || (this.oldFormData['business_type']) || (this.oldFormData['industry_name']);
    if (!!businessType.includes('blog') && (this.currentStep + 1 === this.stepsLength)) {
      this.blogType.emit(true);
    }
    if (this.currentStep === 0 && !businessType.toLowerCase().includes('blog') && !businessType.toLowerCase().includes('vlog')) {
      this._getGeneratedServices(businessType);
    }
    else {
      this.next.emit({formData: this.formGroup.value, domainInfo: this.domainInfo});
    }
  }

  private _generateDomainInfo(domainName): void {
    const domainInfo = {
      "want_domain": this.showDomainInput === 'no' ? 1 : 0,
      "domain_name": domainName || ""
    };
    this.domainInfo = JSON.stringify(domainInfo);
    localStorage.setItem('dbOnboardingDomainInfo', this.domainInfo);
  }

  public domainShow(answer): void {
    this.showDomainInput = answer;
    this._sendEvent('Dashboard Action', this.showDomainInput, 'Generative AI - Do you own a domain');
  }

  private _sendEvent(category: string, label: string, action = 'Generative AI - Question answered'): void {
    let analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      companyType: this.formGroup.get('business_type') && this.formGroup.get('business_type').value || (this.oldFormData['business_type']) || (this.oldFormData['industry_name']),
      websiteType: this.isEcommerce ? 'Ecommerce' : 'Business'
    };

    if (this.oldFormData['template_id']) {
      analyticsData = Object.assign({}, analyticsData, {
        template: this.oldFormData['template_id'],
      });
    }

    if (!this.user) {
      this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData);
    }
    else {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }

  private _getRecreateOngoingActivity(): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        takeUntil(this._destroyStream$),
        filter(activity =>
          activity && activity.websiteId && activity.action && activity.action === ActivityAction.AI_GENERATE_WEBSITE)
      )
      .subscribe(activity => {
        if (activity.status) {
          if (activity.status === ActivityStatus.DONE) {
            this._productService.updateScreenShot(activity.websiteId);
          }
          else if (activity.status === ActivityStatus.FAILED) {
            this.recreationProcess = false;
            this.loading = false;
            NotifierComponent.notify(new Notification('Failed to create a template', NotificationType.DANGER));
          }
        }
      });
  }

  public ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }
  public backToPreviousStep(): void {
    this.loading = false;
    this.back.emit(this.formGroup.value);
  }

  private _getGeneratedServices(businessType): void {
    this.loading = true;
    const language = this.formGroup.get('content_language') && this.formGroup.get('content_language').value || 'english';
    this.reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (token) => {
      this._builderService.generateService(this.formGroup.value, businessType, token, language, !!this.isEcommerce)
        .pipe(filter((response: DbResponse): boolean => !!response),
          first())
        .subscribe((response) => {
          if (response.success && response.data) {
            this.generatedServices = response.data;
            response.data && this.formGroup && this.formGroup.addControl('service_generated', new FormControl(JSON.stringify(response.data)));
            this.currentStep++;
            this.next.emit({formData: this.formGroup.value, domainInfo: this.domainInfo});
          }
          else {
            this.serverErrors = response.response ? response.response['error'] : "";
          }
          this.loading = false;
        });
    });
  }

  private _fillInputValue(field): void {
    for (const key in field) {
      if (!this.showMetaTitle) {
        this.showMetaTitle = field && key && field[key] && this.formGroup.get(key) && field[key] !== '';
      }
      field && key && field[key] && this.formGroup.get(key) && this.formGroup.controls[key].setValue(field[key]);
    }
  }

  public generateDescription(text, fieldName): void {

    this.generateDescriptionLoading = true;
    this.reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (token) => {
      const businessType = this.formGroup.get('business_type') && this.formGroup.get('business_type').value || (this.oldFormData['business_type']);
      const language = this.formGroup.get('content_language') && this.formGroup.get('content_language').value || 'english';
      const label = JSON.stringify(Object.keys(this.formGroup.value));
      this._sendEvent(this.isOnboarding ? 'Onboarding' : 'Dashboard Action', label, 'Generative AI - Question Enhance');
      this._builderService.generateDescription(text, businessType, token, !!this.isEcommerce, language)
        .pipe(filter((response: DbResponse): boolean => !!response),
          first())
        .subscribe((response) => {
          if (response.success && response.data) {
            this.formGroup && this.formGroup.addControl('enhanced_description', new FormControl(response.data));
            this.formGroup.get(fieldName) && this.formGroup.controls[fieldName].setValue(response.data);
          }
          else {
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
          }
          this.generateDescriptionLoading = false;
        });
    });

  }
}
